import Image from "next/image";
import { NexusProduct } from "@/types/nexus-product";
import { useModalContext } from "@/app/context/QuickViewModalContext";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/redux/store";
import { updateQuickView } from "@/redux/features/quickView-slice";
import { getNexusBasePrice } from "@/components/Common/helpers/nexusVariants";
import { useEcommerce } from "@/components/EcommerceProvider/EcommerceProvider";
import {
  resolveProductCardPromotion,
  formatPromotionBadgeLabel,
} from "@/utils/promotions";
import { formatPrice } from "@/utils/formatePrice";

interface NexusProductResultProps {
  product: NexusProduct;
  setSearchModalOpen: (open: boolean) => void;
  host: string | null;
}

export default function NexusProductResult({
  product,
  setSearchModalOpen,
  host,
}: NexusProductResultProps) {
  const { openModal } = useModalContext();
  const dispatch = useDispatch<AppDispatch>();
  const ecommerce = useEcommerce();

  const basePrice = getNexusBasePrice(product);
  const cardPromotion = resolveProductCardPromotion(
    product,
    ecommerce.promotions ?? []
  );

  const handleProductClick = () => {
    setSearchModalOpen(false);

    dispatch(
      updateQuickView({
        _id: product.id,
        name: product.name,
        price: basePrice,
        discountedPrice: 0,
        description: product.description,
        categories: product.categories,
        reviews: [],
        previewImages: [
          ...(product.image ? [{ image: product.image }] : []),
          ...(product.additionalImages ? product.additionalImages.map(img => ({ image: img })) : []),
        ],
        host,
        nexusProduct: product,
        promotions: ecommerce.promotions ?? [],
      })
    );

    openModal();
  };

  return (
    <div
      className="w-full p-2 mb-2 bg-white result-template group rounded-xl hover:bg-gray-2 cursor-pointer"
      onClick={handleProductClick}
    >
      <div className="flex items-center gap-3">
        {product.image && (
          <div className="relative overflow-hidden flex items-center justify-center rounded-lg border border-gray-3 bg-gray-2 w-[110px] h-[84px] shrink-0">
            <Image
              src={product.image}
              alt={product.name}
              fill
              sizes="110px"
              className="object-cover"
            />
            {cardPromotion && (
              <span className="absolute top-1 left-1 text-white text-[0.55rem] font-bold tracking-widest uppercase px-2 py-0.5 rounded bg-red">
                {formatPromotionBadgeLabel(cardPromotion.promotion)}
              </span>
            )}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h4 className="text-base font-semibold text-dark duration-300 sm:text-lg mb-1 truncate">
            {product.name}
          </h4>
          {product.description && (
            <p className="text-sm text-gray-6 mb-2 line-clamp-2">
              {product.description.replace(/<[^>]*>/g, "")}
            </p>
          )}
          {cardPromotion ? (
            <span className="flex items-center gap-2 text-base font-semibold">
              <span className="text-dark-4 line-through font-normal">
                {formatPrice(basePrice)}
              </span>
              <span className="text-red">
                {formatPrice(cardPromotion.discountedPriceGrosze / 100)}
              </span>
            </span>
          ) : (
            <span className="text-base font-semibold text-dark">
              {formatPrice(basePrice)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
